// useSpaceHoldPan — Space hold 中の一時的「手のひら (pan)」モードを提供する共有フック。
//
// 由来 (consult 2026-06-08): Classic (ClassicLayout L689-748) に実装済の Space-hold pan を
// Modern にも入れるための逐語抽出。Classic の挙動と完全に一致させる:
//   - `document` (window) の keydown/keyup で `e.code === 'Space'` を監視し `spaceHeld` 管理。
//   - 修飾キー (Ctrl/Meta/Alt/Shift) 同時押しは無視 (= 別ショートカット衝突回避)。
//   - input/textarea/select/contenteditable に focus 時は Space を通常の文字入力として扱う
//     (= チャット欄を壊さない)。
//   - Space keydown で **preventDefault()** を呼ぶ。これは (a) browser default の page scroll
//     抑止 + (b) フォーカス中ボタン/リンクの既定 Space 起動 (click) の抑止を兼ねる。
//   auto-repeat (`e.repeat`) でも preventDefault を呼び、spaceHeld も立てる (idempotent。別アプリ側で押し始めた Space は最初に届く keydown が repeat になる)
//     (= 長押し中 scroll が漏れる Classic 2026-05-16 fb の罠を踏襲して回避)。
//   - keyup / window blur / visibilitychange (hidden) で **必ず spaceHeld を false に戻す**
//     (= 押しっぱなし誤検知を防ぐ cleanup)。
//
// 出力: `isPanning = spaceHeld || panToggleOn` を effect で `setPanActive` に流す。
//   - `panToggleOn`: ツールバー「手のひら」持続トグル (= iPad 救済 / 明示的 ON-OFF)。
//     Space hold とは独立。Space を放しても toggle ON の間は pan を継続する。
//   - `setPanActive`: 共有コントローラ (useRoomChrome.setPanActive)。CanvasStage がこれを
//     読んでドラッグ pan を行う (§9.7)。
//
// クリーンアーキ: presentation 層の hook。engine/protocol には触れない (pan は scroll 操作)。

import { useEffect, useState } from 'react';

/**
 * focus 中の要素が「文字入力系」か (= Space を通常入力として扱うべきか)。
 * Classic ClassicLayout の isInputFocus を逐語踏襲 (INPUT/TEXTAREA/SELECT/contentEditable)。
 * 純関数として切り出し、DOM 不要なロジックテストを可能にする。
 */
export const isTextInputFocus = (target: EventTarget | null): boolean => {
  if (target === null) return false;
  const el = target as HTMLElement;
  if (el.tagName === 'INPUT') {
    // range / checkbox / radio / color 等は Space で文字が入らない (= 「文字入力系」ではない)。
    // ここを INPUT 一律で true にすると、サイズスライダー等を操作した直後 (focus が
    // <input type=range> に残る) に Space を押しても pan にならず線が引かれる不具合になる。
    const type = ((el as HTMLInputElement).type ?? 'text').toLowerCase();
    return !NON_TEXT_INPUT_TYPES.has(type);
  }
  return el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable === true;
};

/** Space が文字入力にならない <input type> (= Space-hold pan を優先してよい)。 */
const NON_TEXT_INPUT_TYPES: ReadonlySet<string> = new Set([
  'range',
  'checkbox',
  'radio',
  'color',
  'button',
  'submit',
  'reset',
  'image',
  'file',
  'number',
]);

/**
 * keydown が Space-hold pan の「pan 開始トリガ」として処理対象かを判定する純関数。
 * Classic の onKeyDown 早期 return 群を逐語抽出:
 *   - `e.code === 'Space'` 以外は対象外。
 *   - 修飾キー同時押しは対象外。
 *   - 文字入力系に focus 時は対象外。
 * 対象 (true) のときだけ呼び出し側は preventDefault + spaceHeld 更新を行う。
 */
export const isSpacePanKeyDown = (e: KeyboardEvent): boolean => {
  if (e.code !== 'Space') return false;
  if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return false;
  if (isTextInputFocus(e.target)) return false;
  return true;
};

/** 派生値: 一時 hold とツールバー持続トグルの OR (= 実際に pan 有効か)。 */
export const computeIsPanning = (spaceHeld: boolean, panToggleOn: boolean): boolean =>
  spaceHeld || panToggleOn;

export interface UseSpaceHoldPanOptions {
  /** ツールバー「手のひら」持続トグル (= Space hold と独立の 2 つ目の入力ソース)。 */
  readonly panToggleOn: boolean;
  /** pan 有効状態を共有コントローラへ流す sink (= useRoomChrome.setPanActive)。 */
  readonly setPanActive: (next: boolean) => void;
}

export interface UseSpaceHoldPanResult {
  /** Space hold 中フラグ (= cursor 表示等の派生に使える。pan 判定は isPanning)。 */
  readonly spaceHeld: boolean;
  /** spaceHeld || panToggleOn (= setPanActive に流れる値と同一)。 */
  readonly isPanning: boolean;
}

/**
 * Space-hold pan フック。`document` (window) keydown/keyup を listen し、
 * `spaceHeld || panToggleOn` を `setPanActive` に流す。Classic の挙動を逐語踏襲。
 */
export const useSpaceHoldPan = ({
  panToggleOn,
  setPanActive,
}: UseSpaceHoldPanOptions): UseSpaceHoldPanResult => {
  const [spaceHeld, setSpaceHeld] = useState<boolean>(false);
  const isPanning = computeIsPanning(spaceHeld, panToggleOn);

  // pan の有効状態を共有コントローラへ sync (= CanvasStage が読んでドラッグ pan)。
  useEffect(() => {
    setPanActive(isPanning);
  }, [isPanning, setPanActive]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (!isSpacePanKeyDown(e)) return;
      // page scroll 抑止 + フォーカス中ボタン/リンクの既定 Space 起動 (click) 抑止。
      // 2026-05-16 fb 踏襲: preventDefault を `e.repeat` early-return より先に呼ぶ
      // (= 長押し auto-repeat 中も scroll を握る)。
      e.preventDefault();
      // repeat でも立てる (idempotent): 別アプリ側で押し始めた Space は、絵チャに届く最初の
      // keydown が repeat=true になる (実機報告 fix/space-pan-window-activation)。
      setSpaceHeld(true);
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      if (e.code !== 'Space') return;
      // input focus 中でも release は処理 (= 落ちなくする)。spaceHeld は input focus
      // 時に立たないので setSpaceHeld(false) は冪等で安全。panToggleOn は触らない。
      setSpaceHeld(false);
    };
    // 押しっぱなし誤検知を防ぐ cleanup: window が focus を失う / タブが hidden に
    // なると keyup を取りこぼし spaceHeld が立ちっぱなしになり得る。必ず false に戻す。
    const onBlur = (): void => setSpaceHeld(false);
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') setSpaceHeld(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return { spaceHeld, isPanning };
};
